/**
 * The `/feed` filter sidebar (FEED_SPEC § Filter sidebar).
 *
 * A sticky island of accordion sections with a pinned footer. It owns no data: the caller hands it
 * the filters, the group/profile lists and the open-section set, and gets back a new filter object.
 * That is what lets the same component render as a left island above 1100px and as a drawer below
 * it — and what keeps the open/closed state alive across the switch, since it lives one level up.
 */

import type { ContentType, FeedFiltersState, GroupDTO, PostType, ProfileRowDTO } from '@lw/shared';
import { cx } from '../../components';
import {
  FeedAttributesSection,
  FeedContentTypesSection,
  FeedGroupsSection,
  FeedLengthSection,
  FeedPerformanceSection,
  FeedPostTypesSection,
  FeedProfilesSection,
} from './FeedSections';
import { SidebarSection } from './SidebarSection';
import {
  FEED_SECTIONS,
  FEED_SECTION_LABEL,
  clearSection,
  sectionCount,
  totalFilterCount,
  type FeedSectionId,
} from './feedState';

export type FeedSidebarProps = {
  filters: FeedFiltersState;
  onChange: (next: FeedFiltersState) => void;
  groups: readonly GroupDTO[];
  /** Active profiles only. */
  profiles: readonly ProfileRowDTO[];
  mediaEnabled: boolean;
  countedPostTypes: readonly PostType[];
  openSections: ReadonlySet<FeedSectionId>;
  onToggleSection: (section: FeedSectionId) => void;
  className?: string;
};

export function FeedSidebar({
  filters,
  onChange,
  groups,
  profiles,
  mediaEnabled,
  countedPostTypes,
  openSections,
  onToggleSection,
  className,
}: FeedSidebarProps) {
  const listPatch = <K extends 'groupIds' | 'profileIds' | 'postTypes' | 'contentTypes'>(
    key: K,
    next: NonNullable<FeedFiltersState[K]>,
  ) => {
    const updated: FeedFiltersState = { ...filters };
    if (next.length === 0) delete updated[key];
    else updated[key] = next;
    onChange(updated);
  };

  const total = totalFilterCount(filters);

  const body = (section: FeedSectionId) => {
    switch (section) {
      case 'groups':
        return (
          <FeedGroupsSection
            groups={groups}
            selected={filters.groupIds ?? []}
            onChange={(next) => listPatch('groupIds', next)}
          />
        );
      case 'profiles':
        return (
          <FeedProfilesSection
            profiles={profiles}
            selected={filters.profileIds ?? []}
            onChange={(next) => listPatch('profileIds', next)}
            mediaEnabled={mediaEnabled}
          />
        );
      case 'postTypes':
        return (
          <FeedPostTypesSection
            selected={filters.postTypes ?? []}
            onChange={(next: PostType[]) => listPatch('postTypes', next)}
            countedPostTypes={countedPostTypes}
          />
        );
      case 'contentTypes':
        return (
          <FeedContentTypesSection
            selected={filters.contentTypes ?? []}
            onChange={(next: ContentType[]) => listPatch('contentTypes', next)}
          />
        );
      case 'performance':
        return (
          <FeedPerformanceSection
            value={filters.perf ?? []}
            onChange={(next) => {
              const updated: FeedFiltersState = { ...filters };
              if (next.length === 0) delete updated.perf;
              else updated.perf = next;
              onChange(updated);
            }}
          />
        );
      case 'attributes':
        return (
          <FeedAttributesSection
            value={{
              ...(filters.hasMedia === undefined ? {} : { hasMedia: filters.hasMedia }),
              ...(filters.edited === undefined ? {} : { edited: filters.edited }),
              ...(filters.hasLink === undefined ? {} : { hasLink: filters.hasLink }),
            }}
            onChange={(next) => {
              const updated: FeedFiltersState = { ...filters };
              for (const key of ['hasMedia', 'edited', 'hasLink'] as const) {
                if (next[key] === undefined) delete updated[key];
                else updated[key] = next[key];
              }
              onChange(updated);
            }}
          />
        );
      case 'length':
        return (
          <FeedLengthSection
            value={{
              ...(filters.lenUnit === undefined ? {} : { unit: filters.lenUnit }),
              ...(filters.lenMin === undefined ? {} : { min: filters.lenMin }),
              ...(filters.lenMax === undefined ? {} : { max: filters.lenMax }),
            }}
            onChange={(next) => {
              const updated: FeedFiltersState = { ...filters };
              if (next.unit === undefined) {
                delete updated.lenUnit;
                delete updated.lenMin;
                delete updated.lenMax;
              } else {
                updated.lenUnit = next.unit;
                if (next.min === undefined) delete updated.lenMin;
                else updated.lenMin = next.min;
                if (next.max === undefined) delete updated.lenMax;
                else updated.lenMax = next.max;
              }
              onChange(updated);
            }}
          />
        );
    }
  };

  return (
    <div className={cx('feed-sidebar', className)}>
      <div className="feed-sidebar__scroll">
        {FEED_SECTIONS.map((section) => (
          <SidebarSection
            key={section}
            title={FEED_SECTION_LABEL[section]}
            open={openSections.has(section)}
            onToggle={() => onToggleSection(section)}
            count={sectionCount(filters, section)}
            onClear={() => onChange(clearSection(filters, section))}
          >
            {body(section)}
          </SidebarSection>
        ))}
      </div>

      {/* Date range and sort are header state and are deliberately not counted here. */}
      <footer className="feed-sidebar__foot">
        {total === 0 ? (
          <span className="muted">No filters applied</span>
        ) : (
          <button
            type="button"
            className="btn-ghost btn-ghost--sm"
            onClick={() => onChange({})}
          >
            Clear {total} {total === 1 ? 'filter' : 'filters'}
          </button>
        )}
      </footer>
    </div>
  );
}

export default FeedSidebar;
